import "./trax-page-header.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Popover } from "@tql/ui-components-react/kendo/tooltip";
import { Skeleton } from "@tql/ui-components-react/tql/indicators";
import { helpCircle, xClose } from "@tql/ui-components-react/untitled/icons";
import { useRef, useState } from "react";

import { useBroker } from "~/features/ftl/ftl-header/hooks/use-broker.tsx";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { usePopupOutsideClick } from "~/shared/hooks/use-popup-outside-click.ts";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";

type TraxPageHeader = {
	title: string;
	showSupportMessage?: boolean;
	representativeType?: "ltl" | "ftl";
	isLtlOnly?: boolean;
	hideRepresentativeOnMobile?: boolean;
};

export const SUPPORT_CONTACT_TOOLTIP =
	"TQL TRAX Support: TRAX@TQL.com | (800) 580-3101 ext. 56400";

const getRepresentativeLabel = (representativeType: "ltl" | "ftl") =>
	representativeType === "ltl" ? "LTL Representative" : "FTL Representative";

const RepresentativeContact = ({
	representativeType,
	className,
}: {
	representativeType: "ltl" | "ftl";
	className?: string;
}) => {
	const { broker, isLoading, error } = useBroker(
		representativeType === "ltl" ? "LTL" : "FTL",
	);

	const label = `${getRepresentativeLabel(representativeType)}: `;

	const contactMessageClassName = className
		? `trax-page-header-contact-message ${className}`
		: "trax-page-header-contact-message";

	if (isLoading) {
		return (
			<div className={contactMessageClassName}>
				<span className={"trax-page-header-contact-label"}>{label}</span>
				<span
					className={"trax-page-header-representative-skeleton"}
					data-testid={"representative-contact-skeleton"}
				>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
		);
	}

	const extension = broker?.extension?.trim();
	if (error || !broker || !extension) {
		return null;
	}

	return (
		<div className={contactMessageClassName}>
			<span className={"trax-page-header-contact-label"}>{label}</span>
			<a href={`tel:8005803101,${extension}`}>
				{`(800) 580-3101 ext. ${extension}`}
			</a>
		</div>
	);
};

const NeedAssistanceButton = ({
	isLtlOnly,
	representativeType,
	isInternal,
}: {
	isLtlOnly: boolean;
	representativeType?: "ltl" | "ftl";
	isInternal: boolean;
}) => {
	const [showAssistancePopover, setShowAssistancePopover] = useState(false);
	const assistanceAnchor = useRef<HTMLDivElement>(null);
	const assistancePopoverContent = useRef<HTMLDivElement>(null);
	const isMobile = useIsMobile();

	usePopupOutsideClick({
		enabled: showAssistancePopover,
		anchorRef: assistanceAnchor,
		popupRef: assistancePopoverContent,
		onOutsideClick: () => {
			setShowAssistancePopover(false);
		},
	});

	return (
		<div
			className={
				"trax-page-header-need-assistance trax-page-header-mobile-support"
			}
		>
			{isLtlOnly ? null : (
				<span className={"trax-page-header-mobile-need-assistance-text"}>
					Need Assistance
				</span>
			)}
			<div ref={assistanceAnchor}>
				<button
					aria-expanded={showAssistancePopover}
					aria-label={"Show TQL TRAX support contact information"}
					className={"trax-page-header-support-tooltip"}
					data-testid={"trax-page-header-support-tooltip"}
					onClick={() => {
						setShowAssistancePopover(!showAssistancePopover);
					}}
					type={"button"}
				>
					<SvgIcon icon={helpCircle} size={"xlarge"} />
				</button>
			</div>
			<Popover
				anchor={assistanceAnchor.current}
				animate={false}
				callout={false}
				className={"trax-page-header-assistance-popover"}
				onClose={() => {
					setShowAssistancePopover(false);
				}}
				position={"bottom"}
				show={showAssistancePopover}
			>
				<div ref={assistancePopoverContent}>
					<div className={"trax-page-header-assistance-popover-title"}>
						Need Assistance?
						<button
							aria-label={"Close"}
							className={"trax-page-header-assistance-popover-close"}
							onClick={() => {
								setShowAssistancePopover(false);
							}}
							type={"button"}
						>
							<SvgIcon icon={xClose} size={"small"} />
						</button>
					</div>
					<div className={"trax-page-header-assistance-popover-body"}>
						<div className={"trax-page-header-contact-message"}>
							<span className={"trax-page-header-contact-label"}>
								TQL TRAX Support:
							</span>
							{isMobile ? <br /> : null}{" "}
							<a href={"mailto:TRAX@TQL.com"}>TRAX@TQL.com</a>
							{" | "}
							<a href={"tel:8005803101,00000"}>(800) 580-3101 ext. 56400</a>
						</div>
						{representativeType && !isInternal ? (
							<RepresentativeContact representativeType={representativeType} />
						) : null}
					</div>
				</div>
			</Popover>
		</div>
	);
};

export const TraxPageHeader = ({
	title,
	showSupportMessage,
	representativeType,
	isLtlOnly = false,
	hideRepresentativeOnMobile = false,
}: TraxPageHeader) => {
	// The support message and representative contact are only relevant for
	// external users. Gating here also prevents the broker API call
	// (RepresentativeContact -> useBroker) from firing for internal users.
	const isInternal = getIsInternal();

	return (
		<div className={"trax-page-header-wrapper"}>
			<div className={"trax-page-header-title-wrapper"}>
				<h2>{title}</h2>
				{isLtlOnly ? (
					<div
						className={
							"trax-page-header-ltl-pill trax-page-header-mobile-ltl-pill"
						}
					>
						LTL only
					</div>
				) : null}
				{showSupportMessage && !isInternal ? (
					<div className={"trax-page-header-mobile-actions"}>
						<NeedAssistanceButton
							isInternal={isInternal}
							isLtlOnly={isLtlOnly}
							representativeType={representativeType}
						/>
					</div>
				) : null}
			</div>
			{isLtlOnly ? (
				<div
					className={
						"trax-page-header-ltl-pill-container trax-page-header-desktop-ltl-pill"
					}
				>
					<div className={"trax-page-header-ltl-pill"}>LTL only</div>
				</div>
			) : null}
			<div className={"trax-page-header-rest"}>
				{showSupportMessage && !isInternal ? (
					<>
						<div className={"trax-page-header-need-assistance"}>
							Need Assistance?
						</div>
						<div
							className={
								"trax-page-header-contact-message trax-page-header-desktop-support-contact"
							}
						>
							<span className={"trax-page-header-contact-label"}>
								TQL TRAX Support:
							</span>{" "}
							<a href={"mailto:TRAX@TQL.com"}>TRAX@TQL.com</a>
							{" | "}
							<a href={"tel:8005803101,00000"}>(800) 580-3101 ext. 56400</a>
						</div>
					</>
				) : null}
				{representativeType && !isInternal ? (
					<RepresentativeContact
						className={
							hideRepresentativeOnMobile
								? "trax-page-header-representative-hide-mobile"
								: undefined
						}
						representativeType={representativeType}
					/>
				) : null}
			</div>
		</div>
	);
};
